// src/components/quotes/quoteLineDetail.js
//
// The per-line-item `detail` field: CUSTOMER-FACING long-form text shown under
// the item's description on the PDF and the web quote.
//
// ⛔ IT IS CALLED `detail`, NOT `notes`, ON PURPOSE. A line item already carries
// `supplierNotes` and the quote carries `q.notes`; both are internal and the
// backend renderer asserts they never reach a customer. This field is the
// opposite — deliberately printed — and a `notes` beside `supplierNotes` on the
// same object is an invitation to render the wrong one or "tidy up" by merging
// them. Keep the name, and read it through lineDetailOf so an item saved before
// the field existed is an absent block, never "undefined" in the output.
//
// Pure, React-free, no imports — testable without MSAL behind MspOsApp.

/** The detail text, "" for an item that predates the field. */
export const lineDetailOf = (li) => (li && typeof li.detail === "string" ? li.detail : "");

const hasDetail = (li) => !!li && typeof li.detail === "string";

const fill = (items) => (items || []).map((li) => (hasDetail(li) ? li : { ...li, detail: "" }));

/**
 * The quote with `detail: ""` backfilled onto every line item that lacks it —
 * the sections' options and the common items alike.
 *
 * ⚠ RETURNS THE SAME OBJECT REFERENCE when nothing is missing, and that is
 * load-bearing: ensureMigrated runs on every read, and a fresh reference each
 * time would hand React a changed quote on every render and make the debounced
 * save fire forever. withFulfilment and withQuoteTerms carry the same contract
 * for the same reason.
 *
 * ⛔ APPLIED TO THE RESULT OF THE SHAPE MIGRATION, NEVER INSIDE IT.
 * shapeMigrateQuote early-returns on `Array.isArray(q.sections)` — true of
 * nearly every stored quote — so a backfill inside its branches would reach
 * only the handful that still predate sections. See withFulfilment.
 */
export function withLineItemDetail(quote) {
  if (!quote) return quote;
  const sections = Array.isArray(quote.sections) ? quote.sections : [];
  const commons = Array.isArray(quote.commonLineItems) ? quote.commonLineItems : [];
  const complete = commons.every(hasDetail)
    && sections.every((s) => (s.options || []).every((o) => (o.lineItems || []).every(hasDetail)));
  if (complete) return quote;
  return {
    ...quote,
    sections: sections.map((s) => ({
      ...s,
      options: (s.options || []).map((o) => ({ ...o, lineItems: fill(o.lineItems) })),
    })),
    commonLineItems: fill(commons),
  };
}
